import React, { useState, useEffect } from "react";
import qs from 'query-string';
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import CountryCodeSelector from "../../components/CountryCodeSelector";
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Fade,
  Slide,
  Badge,
  IconButton
} from "@material-ui/core";
import {
  LockOutlined,
  CheckCircle,
  Business,
  Phone,
  Email,
  VpnKey,
  ArrowBack,
  Star
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import ColorModeContext from "../../layout/themeContext";
import UserLanguageSelector from "../../components/UserLanguageSelector";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { useContext } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";

const Copyright = () => {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href="#">
        Whaticket
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    background: theme.palette.background.default,
  },
  
  // Seção Hero (Esquerda - Visual/Marketing)
  heroSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #171717 0%, #2d2d2d 100%)"
      : "linear-gradient(135deg, #3ecc96 0%, #2de195 100%)",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  
  bgPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "32px 32px",
    zIndex: 0,
  },
  
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#fff",
    animation: "$fadeInLeft 0.8s ease-out",
  },
  
  heroLogo: {
    width: "80%",
    maxWidth: 400,
    height: "auto",
    marginBottom: theme.spacing(4),
    filter: "brightness(0) invert(1)",
  },
  
  heroTitle: {
    fontWeight: 700,
    fontSize: "2.5rem",
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
  },
  
  heroSubtitle: {
    fontSize: "1.25rem",
    opacity: 0.9,
    fontWeight: 400,
    maxWidth: 500,
    margin: "0 auto",
  },

  // Seção de Formulário (Direita)
  formSection: {
    width: "100%",
    maxWidth: 600,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  
  formContainer: {
    width: "100%",
    maxWidth: 480,
    margin: "auto",
    animation: "$fadeInRight 0.8s ease-out",
  },
  
  mobileLogo: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: 220,
      margin: "0 auto 32px",
    },
  },
  
  welcomeText: {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  
  formTitle: {
    fontWeight: 700,
    fontSize: "1.8rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  
  formSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  
  inputField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      }
    }
  },
  
  submitButton: {
    margin: theme.spacing(2, 0, 3),
    padding: theme.spacing(1.8),
    borderRadius: 12,
    fontWeight: 700,
    fontSize: "1rem",
    textTransform: "none",
    boxShadow: "0 4px 14px 0 rgba(62, 204, 150, 0.39)",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(62, 204, 150, 0.45)",
    },
  },
  
  linkText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },
  
  footerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    marginTop: theme.spacing(2),
    textAlign: "center",
  },

  topActions: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    zIndex: 10,
  },
  
  actionButton: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: 12,
    boxShadow: theme.palette.type === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
    color: theme.palette.type === "dark" ? "#fff" : theme.palette.text.primary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#fff",
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },

  // Planos
  pricingCard: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.type === "dark" ? "rgba(62, 204, 150, 0.05)" : "rgba(62, 204, 150, 0.02)",
    }
  },
  pricingCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.type === "dark" ? "rgba(62, 204, 150, 0.1)" : "rgba(62, 204, 150, 0.05)",
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
  planName: {
    fontWeight: 700,
    fontSize: "1.1rem",
  },
  planPrice: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
  },

  stepper: {
    padding: theme.spacing(2, 0, 4),
    background: "transparent",
    "& .MuiStepIcon-root.MuiStepIcon-active": { color: theme.palette.primary.main },
    "& .MuiStepIcon-root.MuiStepIcon-completed": { color: theme.palette.primary.main },
  },

  // Keyframes
  "@keyframes fadeInLeft": {
    "0%": { opacity: 0, transform: "translateX(-30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  },
  "@keyframes fadeInRight": {
    "0%": { opacity: 0, transform: "translateX(30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  }
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("signup.validation.nameTooShort"))
    .max(50, i18n.t("signup.validation.nameTooLong"))
    .required(i18n.t("signup.validation.required")),
  password: Yup.string()
    .min(5, i18n.t("signup.validation.passwordTooShort"))
    .max(50, i18n.t("signup.validation.passwordTooLong"))
    .required(i18n.t("signup.validation.required")),
  email: Yup.string()
    .email(i18n.t("signup.validation.invalidEmail"))
    .required(i18n.t("signup.validation.required")),
  phone: Yup.string()
    .min(10, i18n.t("signup.validation.phoneIncomplete"))
    .max(20, i18n.t("signup.validation.phoneTooLong"))
    .required(i18n.t("signup.validation.required")),
});

const SignUp = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [allowregister, setallowregister] = useState('enabled');
  const [trial, settrial] = useState('3');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const logoLight = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/interno.png`;
  const logoDark = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/logo_w.png`;

  let companyId = null;

  useEffect(() => {
    fetchallowregister();
    fetchtrial();
  }, []);

  const fetchtrial = async () => {
    try {
      const responsevvv = await api.get("/settings/trial");
      const allowtrialX = responsevvv.data.value;
      settrial(allowtrialX);
    } catch (error) {
      console.error('Error retrieving trial', error);
    }
  };

  const fetchallowregister = async () => {
    try {
      const responsevv = await api.get("/settings/allowregister");
      const allowregisterX = responsevv.data.value;
      setallowregister(allowregisterX);
    } catch (error) {
      console.error('Error retrieving allowregister', error);
    }
  };

  if (allowregister === "disabled") {
    history.push("/login");
  }

  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    phone: "",
    password: "",
    planId: selectedPlan?.id || ""
  };

  const dueDate = moment().add(trial, "day").format();

  const handleSignUp = async values => {
    Object.assign(values, {
      recurrence: "MENSAL",
      dueDate: dueDate,
      status: "t",
      campaignsEnabled: true,
      planId: selectedPlan.id
    });

    try {
      await openApi.post("/companies/cadastro", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const [plans, setPlans] = useState([]);
  const { register: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    handleNext();
  };

  const appName = process.env.REACT_APP_NAME_SYSTEM || "Whaticket";
  const steps = [i18n.t("signup.steps.selectPlan"), i18n.t("signup.steps.createAccount")];

  // Identificar o plano mais popular (com maior valor)
  const popularPlan = plans.length > 0
    ? plans.reduce((prev, current) =>
      (prev.value > current.value) ? prev : current)
    : null;

  const logo = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/login.png`;
  const logoWithRandom = `${logo}?r=${Math.random()}`;

  return (
    <div className={classes.root}>
      {/* Botões de Tema e Idioma */}
      <div className={classes.topActions}>
        <IconButton 
          className={classes.actionButton}
          onClick={colorMode.toggleColorMode}
          edge="end"
        >
          {theme.palette.type === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        
        <div className={classes.actionButton} style={{ borderRadius: 12, overflow: 'hidden' }}>
          <UserLanguageSelector />
        </div>
      </div>

      {/* Lado Esquerdo - Hero Section */}
      <div className={classes.heroSection}>
        <div className={classes.bgPattern} />
        <div className={classes.heroContent}>
          <img
            src={logoWithRandom}
            alt="Logo Hero"
            className={classes.heroLogo}
          />
          <Typography className={classes.heroTitle}>
            {i18n.t("signup.hero.title")}
          </Typography>
          <Typography className={classes.heroSubtitle}>
            {i18n.t("signup.hero.subtitle", { appName })}
          </Typography>
        </div>
      </div>

      {/* Lado Direito - Form Section */}
      <div className={classes.formSection}>
        <div className={classes.formContainer}>
          <img
            src={logoWithRandom}
            alt="Logo Mobile"
            className={classes.mobileLogo}
          />
          
          <Typography className={classes.welcomeText}>
            {i18n.t("signup.welcomeText")}
          </Typography>
          <Typography className={classes.formTitle}>
            {i18n.t("signup.formTitle")}
          </Typography>
          {trial !== "disabled" && (
            <Typography className={classes.formSubtitle}>
              {i18n.t("signup.formSubtitle", { days: trial })}
            </Typography>
          )}

          <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel={isMobile}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <div className={classes.stepContent}>
            {activeStep === 0 ? (
              <Fade in={activeStep === 0} timeout={500}>
                <div>
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
                    {i18n.t("signup.planSelectionTitle")}
                  </Typography>
                  {plans.map((plan) => (
                    <Card
                      key={plan.id}
                      className={`${classes.pricingCard} ${selectedPlan?.id === plan.id ? classes.pricingCardSelected : ''}`}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      <CardContent style={{ padding: '16px 24px' }}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs>
                            <Typography className={classes.planName}>{plan.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {plan.connections} {i18n.t("signup.planCard.whatsappConnections")} • {plan.users} {i18n.t("signup.planCard.users")}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.planPrice}>
                              {i18n.t("plans.form.money")} {plan.value.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Box mt={4} textAlign="center">
                    <Typography className={classes.footerText}>
                      {i18n.t("signup.alreadyHaveAccount")}{" "}
                      <Link component={RouterLink} to="/login" className={classes.linkText}>
                        {i18n.t("signup.buttons.login")}
                      </Link>
                    </Typography>
                  </Box>
                </div>
              </Fade>
            ) : (
              <Slide direction="left" in={activeStep === 1}>
                <div>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBack}
                    style={{ textTransform: 'none', marginBottom: 16, fontWeight: 600 }}
                    color="primary"
                  >
                    {i18n.t("signup.buttons.backToPlans")}
                  </Button>

                  <Formik
                    initialValues={initialState}
                    enableReinitialize={true}
                    validationSchema={UserSchema}
                    onSubmit={(values, actions) => {
                      setTimeout(() => {
                        handleSignUp(values);
                        actions.setSubmitting(false);
                      }, 400);
                    }}
                  >
                    {({ touched, errors, isSubmitting }) => (
                      <Form className={classes.form}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              variant="outlined"
                              fullWidth
                              id="name"
                              label={i18n.t("signup.form.companyName")}
                              name="name"
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                              required
                              className={classes.inputField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              variant="outlined"
                              fullWidth
                              id="email"
                              label={i18n.t("signup.form.email")}
                              name="email"
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                              required
                              className={classes.inputField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                             <Box mb={2}>
                               <Field name="phone">
                                {({ field, form }) => (
                                  <CountryCodeSelector
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      form.setFieldValue("phone", e.target.value);
                                      form.setFieldTouched("phone", true);
                                    }}
                                    error={touched.phone && Boolean(errors.phone)}
                                    helperText={touched.phone && errors.phone}
                                  />
                                )}
                              </Field>
                             </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              variant="outlined"
                              fullWidth
                              name="password"
                              label={i18n.t("signup.form.password")}
                              type="password"
                              id="password"
                              error={touched.password && Boolean(errors.password)}
                              helperText={touched.password && errors.password}
                              required
                              className={classes.inputField}
                            />
                          </Grid>
                        </Grid>

                        <Box mt={2} mb={2} textAlign="center">
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("signup.links.agree")}{" "}
                            <Link
                              component={RouterLink}
                              to="/terms"
                              color="primary"
                            >
                              {i18n.t("signup.links.terms")}
                            </Link>{" "}
                            {i18n.t("signup.links.and")}{" "}
                            <Link
                              component={RouterLink}
                              to="/privacy"
                              color="primary"
                            >
                              {i18n.t("signup.links.privacy")}
                            </Link>
                            .
                          </Typography>
                        </Box>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          className={classes.submitButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            trial !== "disabled" 
                              ? i18n.t("signup.buttons.startTrial", { days: trial })
                              : i18n.t("signup.buttons.register")
                          )}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </Slide>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
